import { useState, useEffect, useRef } from "react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Search, X, Zap, Package } from "lucide-react";
import { toast } from "sonner";

export default function BarcodeScanner({ 
  open, 
  onOpenChange, 
  prodottiDisponibili, 
  onProductFound,
  onProductNotFound 
}) {
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [lastScanned, setLastScanned] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const inputRef = useRef(null);

  // Reset quando si apre il dialog
  useEffect(() => {
    if (open) {
      setScanning(false);
      setManualCode("");
      setLastScanned(null);
      setCameraError(null);
    }
  }, [open]);

  // Focus sull'input quando si apre
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleScan = (result) => {
    if (result && result.length > 0) {
      const barcode = result[0].rawValue;
      
      // Evita scansioni ripetute dello stesso codice
      if (barcode === lastScanned) return;
      setLastScanned(barcode);
      
      // Cerca il prodotto
      const prodotto = prodottiDisponibili.find(p => 
        p.codice_barre === barcode || 
        p.codice_prodotto === barcode
      );
      
      if (prodotto) {
        toast.success(`Prodotto trovato: ${prodotto.nome}`);
        onProductFound(prodotto);
        onOpenChange(false);
      } else {
        toast.error(`Codice non trovato: ${barcode}`);
        if (onProductNotFound) {
          onProductNotFound(barcode);
        }
      }
    }
  };

  const handleManualSearch = () => {
    if (!manualCode.trim()) return;
    
    const prodotto = prodottiDisponibili.find(p => 
      p.codice_barre === manualCode.trim() || 
      p.codice_prodotto === manualCode.trim() ||
      p.nome.toLowerCase().includes(manualCode.toLowerCase().trim())
    );
    
    if (prodotto) {
      toast.success(`Prodotto trovato: ${prodotto.nome}`);
      onProductFound(prodotto);
      onOpenChange(false);
    } else {
      toast.error(`Nessun prodotto trovato per: ${manualCode}`);
    }
    
    setManualCode("");
  };

  const handleCameraError = (error) => {
    setCameraError("Impossibile accedere alla fotocamera. Controlla i permessi del browser.");
    setScanning(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-purple-600" />
            Scanner Codice a Barre
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Scanner Camera Area */}
          {scanning ? (
            <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
              <Scanner
                onScan={handleScan}
                onError={handleCameraError}
                formats={['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'qr_code']}
                components={{
                  audio: false,
                  finder: true,
                  torch: true,
                  zoom: true
                }}
                styles={{
                  container: {
                    width: '100%',
                    height: '100%'
                  },
                  video: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }
                }}
              />
              
              {/* Overlay con linea di scansione */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-4 border-2 border-purple-400 rounded-lg">
                  <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-purple-500 animate-pulse"></div>
                </div>
              </div>
              
              {/* Pulsante per fermare la scansione */}
              <Button
                variant="destructive"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => setScanning(false)}
              >
                <X className="w-4 h-4 mr-1" />
                Chiudi Camera
              </Button>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl aspect-video flex flex-col items-center justify-center gap-3">
              {cameraError ? (
                <div className="text-center px-4">
                  <div className="w-12 h-12 mx-auto mb-2 bg-red-100 rounded-full flex items-center justify-center">
                    <X className="w-6 h-6 text-red-500" />
                  </div>
                  <p className="text-sm text-red-600 mb-3">{cameraError}</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setCameraError(null);
                      setScanning(true);
                    }}
                  >
                    Riprova
                  </Button>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                    <Camera className="w-8 h-8 text-purple-600" />
                  </div>
                  <Button 
                    onClick={() => setScanning(true)}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="start-camera-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Avvia Fotocamera
                  </Button>
                  <p className="text-xs text-slate-500">
                    Inquadra il codice a barre del prodotto
                  </p>
                </>
              )}
            </div>
          )}
          
          {/* Divisore */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-400">oppure</span>
            </div>
          </div>
          
          {/* Input Manuale */}
          <div className="space-y-2">
            <p className="text-sm text-slate-600 font-medium">Inserisci manualmente:</p>
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                placeholder="Codice a barre o nome prodotto..."
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleManualSearch();
                  }
                }}
                className="flex-1"
                data-testid="barcode-manual-input"
              />
              <Button 
                onClick={handleManualSearch}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="barcode-search-btn"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Prodotti Recenti/Suggeriti */}
          {prodottiDisponibili.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 font-medium flex items-center gap-1">
                <Package className="w-3 h-3" />
                Prodotti Disponibili ({prodottiDisponibili.length})
              </p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {prodottiDisponibili.slice(0, 5).map(p => (
                  <button
                    key={p.id}
                    onClick={() => {
                      onProductFound(p);
                      onOpenChange(false);
                    }}
                    className="w-full text-left p-2 hover:bg-purple-50 rounded-lg text-sm flex justify-between items-center transition-colors"
                    data-testid={`quick-product-${p.id}`}
                  >
                    <div>
                      <span className="font-medium">{p.nome}</span>
                      {p.codice_barre && (
                        <span className="text-xs text-slate-400 ml-2">({p.codice_barre})</span>
                      )}
                    </div>
                    <span className="text-purple-600 font-bold">€{p.prezzo_vendita || p.prezzo}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
